import { useEffect, useRef, useState } from 'react';

/** Debounce a fast-changing value (search boxes) so we do not hit the API on every keystroke. */
export function useDebouncedValue<T>(value: T, delayMs = 200): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export interface HotkeyOptions {
  /** Fire even when focus is inside an input/textarea (default false, except when a modifier is held). */
  allowInInputs?: boolean;
  enabled?: boolean;
}

/**
 * Global keyboard shortcut, e.g. useHotkey('mod+k', open). "mod" = Ctrl on Windows/Linux, Cmd on macOS.
 * Shortcuts with a modifier work from inside inputs; bare keys do not.
 */
export function useHotkey(combo: string, handler: (event: KeyboardEvent) => void, options: HotkeyOptions = {}): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const { allowInInputs = false, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;
    const parts = combo.toLowerCase().split('+');
    const key = parts[parts.length - 1]!;
    const wantsMod = parts.includes('mod');
    const wantsShift = parts.includes('shift');
    const wantsAlt = parts.includes('alt');

    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== key) return;
      const mod = event.ctrlKey || event.metaKey;
      if (wantsMod !== mod) return;
      if (wantsShift !== event.shiftKey) return;
      if (wantsAlt !== event.altKey) return;
      const target = event.target as HTMLElement | null;
      const typing = !!target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (typing && !allowInInputs && !wantsMod) return;
      handlerRef.current(event);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [combo, allowInInputs, enabled]);
}
